/**
 * Secure Session Manager for PWA
 * Handles token storage, refresh, and session persistence
 */

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const SESSION_KEY = 'session_data';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const REMEMBER_ME_KEY = 'remember_me';

// Session timeout in milliseconds - DISABLED automatic logout
// Both sessions now last 365 days (essentially permanent until manual logout)
const LONG_SESSION_DURATION = 365 * 24 * 60 * 60 * 1000;  // 365 days
const SHORT_SESSION_DURATION = 365 * 24 * 60 * 60 * 1000; // 365 days (same as long)

/**
 * Get the appropriate storage based on remember me preference
 */
function getStorage() {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  return rememberMe ? localStorage : sessionStorage;
}

/**
 * Store authentication token securely
 */
export function setAuthToken(token, rememberMe = false) {
  // Store remember me preference in localStorage
  localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString());
  
  const storage = rememberMe ? localStorage : sessionStorage;
  const expiry = Date.now() + (rememberMe ? LONG_SESSION_DURATION : SHORT_SESSION_DURATION);
  
  storage.setItem(TOKEN_KEY, token);
  storage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
  
  // Also store in memory for faster access
  window.__authToken = token;
}

/**
 * Get authentication token
 */
export function getAuthToken() {
  // Check memory first
  if (window.__authToken) {
    return window.__authToken;
  }
  
  // Check localStorage first (for remember me)
  let token = localStorage.getItem(TOKEN_KEY);
  let expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  
  // If not in localStorage, check sessionStorage
  if (!token) {
    token = sessionStorage.getItem(TOKEN_KEY);
    expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  }
  
  // Check if token is expired
  if (token && expiry) {
    if (Date.now() > parseInt(expiry, 10)) {
      clearAuthData();
      return null;
    }
    window.__authToken = token;
    return token;
  }
  
  return null;
}

/**
 * Store user data
 */
export function setUserData(user) {
  const storage = getStorage();
  storage.setItem(USER_KEY, JSON.stringify(user));
  window.__authUser = user;
}

/**
 * Get user data
 */
export function getUserData() {
  if (window.__authUser) {
    return window.__authUser;
  }
  
  let userData = localStorage.getItem(USER_KEY);
  if (!userData) {
    userData = sessionStorage.getItem(USER_KEY);
  }
  
  if (userData) {
    try {
      const user = JSON.parse(userData);
      window.__authUser = user;
      return user;
    } catch (e) {
      return null;
    }
  }
  
  return null;
}

/**
 * Store session data (any additional session info)
 */
export function setSessionData(data) {
  const storage = getStorage();
  storage.setItem(SESSION_KEY, JSON.stringify({
    ...data,
    lastActivity: Date.now()
  }));
}

/**
 * Get session data
 */
export function getSessionData() {
  let sessionData = localStorage.getItem(SESSION_KEY);
  if (!sessionData) {
    sessionData = sessionStorage.getItem(SESSION_KEY);
  }
  
  if (sessionData) {
    try {
      return JSON.parse(sessionData);
    } catch (e) {
      return null;
    }
  }
  
  return null;
}

/**
 * Update last activity timestamp
 */
export function updateLastActivity() {
  const sessionData = getSessionData() || {};
  setSessionData({
    ...sessionData,
    lastActivity: Date.now()
  });
}

/**
 * Check if session is valid
 */
export function isSessionValid() {
  const token = getAuthToken();
  if (!token) return false;
  
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY) || sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return false;
  
  return Date.now() < parseInt(expiry, 10);
}

/**
 * Extend session expiry
 */
export function extendSession() {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  const duration = rememberMe ? LONG_SESSION_DURATION : SHORT_SESSION_DURATION;
  const newExpiry = Date.now() + duration;
  
  const storage = getStorage();
  storage.setItem(TOKEN_EXPIRY_KEY, newExpiry.toString());
}

/**
 * Clear all authentication data
 */
export function clearAuthData() {
  // Clear from both storages
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  
  // Clear memory
  window.__authToken = null;
  window.__authUser = null;
}

/**
 * Get time until session expires
 */
export function getTimeUntilExpiry() {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY) || sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return 0;
  
  const remaining = parseInt(expiry, 10) - Date.now();
  return Math.max(0, remaining);
}

/**
 * Check if session is about to expire (within 5 minutes)
 */
export function isSessionExpiringSoon() {
  const remaining = getTimeUntilExpiry();
  return remaining > 0 && remaining < 5 * 60 * 1000;
}

/**
 * Session activity tracker - extends session on user activity
 */
let activityTimeout = null;
export function trackActivity() {
  if (activityTimeout) {
    clearTimeout(activityTimeout);
  }
  
  activityTimeout = setTimeout(() => {
    if (isSessionValid()) {
      extendSession();
      updateLastActivity();
    }
  }, 1000); // Debounce activity tracking
}

/**
 * Initialize activity tracking
 */
export function initActivityTracking() {
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  
  events.forEach(event => {
    window.addEventListener(event, trackActivity, { passive: true });
  });
  
  // Return cleanup function
  return () => {
    events.forEach(event => {
      window.removeEventListener(event, trackActivity);
    });
    if (activityTimeout) {
      clearTimeout(activityTimeout);
    }
  };
}

/**
 * Biometric authentication support check
 */
export async function isBiometricAvailable() {
  if (!window.PublicKeyCredential) {
    return false;
  }
  
  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (e) {
    return false;
  }
}

export default {
  setAuthToken,
  getAuthToken,
  setUserData,
  getUserData,
  setSessionData,
  getSessionData,
  isSessionValid,
  extendSession,
  clearAuthData,
  getTimeUntilExpiry,
  isSessionExpiringSoon,
  initActivityTracking,
  isBiometricAvailable
};
